import React, { useState } from 'react';
import { ColorSelect } from '../portal/ColorSelect';
import LogoUploader from '../portal/LogoUploader';
import { useUIStore } from '../../../store/uiStore';
import { PremiumBrandingAlert } from '../portal/PremiumBrandingAlert';

interface HelpCenterBrandingProps {
  settings?: {
    themeColor?: string;
    logoUrl?: string;
    defaultLogoUrl?: string;
    companyUrl?: string;
  };
  isProCloudPlan: boolean;
  onUpdate: (settings: {
    themeColor?: string;
    logoUrl?: string;
    companyUrl?: string;
  }, logoFile?: File | null, removeFile?: boolean) => Promise<void>;
}

// function isValidHttpUrl(str: string | undefined) {
//   if (!str) {
//     return true;
//   }

//   let url;
  
//   try {
//     url = new URL(str);
//   } catch (error) {
//     console.log(error);
//     return false;  
//   }

//   return url.protocol === "http:" || url.protocol === "https:";
// }

export const HelpCenterBranding: React.FC<HelpCenterBrandingProps> = ({ 
  settings, 
  isProCloudPlan,
  onUpdate 
}) => {
  const { setIsSaving } = useUIStore();
  const [localSettings, setLocalSettings] = useState({
    themeColor: settings?.themeColor || '',
    companyUrl: settings?.companyUrl || '',
    logoUrl: settings?.logoUrl || '',
    defaultLogoUrl: settings?.defaultLogoUrl || '',
  });

  // const [validation, setValidation] = useState({
  //   companyUrl: ''
  // });

  const updateSettings = async (
    updates: Partial<typeof localSettings>, 
    logoFile?: File | null, 
    removeFile?: boolean
  ) => {
    const newSettings = { ...localSettings, ...updates };
    setLocalSettings(newSettings);
    
    // Validate settings (currently disabled - company URL field is commented out)
    // const newValidation = {
    //   companyUrl: (newSettings.companyUrl && !isValidHttpUrl(newSettings.companyUrl)) 
    //     ? 'Company URL is incorrect' 
    //     : ''
    // };
    // setValidation(newValidation);
    // 
    // // Don't update if there are validation errors
    // const hasErrors = Object.values(newValidation).some(error => error !== '');
    // if (hasErrors) {
    //   return;
    // }
    
    try {
      setIsSaving(true);
      await onUpdate({
        themeColor: newSettings.themeColor,
        logoUrl: newSettings.logoUrl,
        companyUrl: newSettings.companyUrl,
      }, logoFile, removeFile);
    } catch (error) {
      console.error('Error updating help center branding:', error);
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  };

  const handleThemeColorChange = (themeColor: string) => {
    updateSettings({ themeColor });
  };

  // const handleCompanyUrlChange = (companyUrl: string) => {
  //   updateSettings({ companyUrl });
  // };

  const handleRemoveLogo = () => {
    updateSettings({ logoUrl: '' }, null, true);
  };

  const handleLogoUpload = (file: File) => {
    // Create a preview URL for immediate display
    const url = URL.createObjectURL(file);
    updateSettings({ logoUrl: url }, file, false);
  };

  return (
    <div className="space-y-6">
      {!isProCloudPlan && (
        <PremiumBrandingAlert context="help-center" />
      )}
      <div
        className={`${
          !isProCloudPlan ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-6">
          {/* <div className="space-y-2 flex flex-row items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Company URL
              </label>
              <p className="text-[0.8rem] text-muted-foreground text-wrap mr-2">
                Your website URL (used for logo links and throughout the help center). Leave empty to remove link
              </p>
              {validation.companyUrl && (
                <p className="text-sm text-red-500">{validation.companyUrl}</p>
              )}
            </div>
            <Input
              type="text"
              value={localSettings.companyUrl}
              onChange={(e) => handleCompanyUrlChange(e.target.value)}
              maxLength={200}
              className="h-8 text-gray-500 text-sm border rounded px-2 !w-[250px] ml-auto"
            />
          </div> */}

          <div className="space-y-2 flex flex-wrap flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Help Center Color Scheme
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                Color scheme for this help center
              </p>
            </div>
            <ColorSelect
              value={localSettings.themeColor}
              onChange={handleThemeColorChange}
            />
          </div>

          <div className="space-y-2 flex flex-wrap flex-row items-center justify-between mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Help Center Logo
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                Upload a custom logo for this help center
              </p>
            </div>
            <div className="w-[250px]">
              <LogoUploader
                value={localSettings.logoUrl}
                onChange={(url) => updateSettings({ logoUrl: url })}
                onRemove={handleRemoveLogo}
                onUploadFile={handleLogoUpload}
                defaultLogo={localSettings.defaultLogoUrl}
                maxSize={2}
                maxHeight={1000}
                maxWidth={1000}
                acceptedTypes={["image/jpeg", "image/png"]}
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

